import { useEffect, useState } from "react";
import {
  Box,
  LinearProgress,
  Alert,
  Stack,
  Typography,
  Drawer,
  IconButton,
  Divider,
  Button,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useOrganization } from "..";
import { useAuth } from "../../../../contexts/AuthContext";

import { fetchData } from "../../../../fetchData";

import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { humanFileSize } from "../../../../utils";

const drawerDataDefault = {
  state: false,
  delete: false,
  data: {
    id: "",
    name: "",
    size: 0,
    lastModifiedFormated: "",
    key: "",
  },
};

type Props = {
  path: string | undefined;
  id: string | undefined;
  boxPadding: number;
};

const OrganizationCloudTab = (props: Props) => {
  const { path, id, boxPadding } = props;
  const auth = useAuth();
  const snackbar = useSnackbar();
  const { data: organization } = useOrganization();
  const [drawer, setDrawer] = useState(drawerDataDefault);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<any>({
    folders: [],
    files: [],
  });
  const [accountingDraft, setAccountingDraft] = useState<any>(null);
  const [validating, setValidating] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [organizationData, setOrganizationData] = useState<any>(null);
  const [invalidateDialogOpen, setInvalidateDialogOpen] = useState(false);
  const [invalidateConfirmOpen, setInvalidateConfirmOpen] = useState(false);
  const [invalidateMessage, setInvalidateMessage] = useState("");
  const [validateConfirmOpen, setValidateConfirmOpen] = useState(false);
  const [validationInfoDismissed, setValidationInfoDismissed] = useState(false);

  const decodedPath = path
    ? path.includes("%")
      ? decodeURIComponent(path)
      : path
    : undefined;
  const isAccountingPath =
    decodedPath && String(decodedPath).startsWith("accounting/");

  const handleCloudApi = async () => {
    setLoading(true);
    setError(false);

    try {
      const result = await fetchData(
        `/cloud/${id ? id : organization._id}?path=${path ? path : "cloud"}`,
        {
          method: "GET",
        },
      );

      // console.log(result);
      setData(result);
    } catch (error) {
      console.log(error);
      setError(true);
    }

    setLoading(false);
  };

  const handleError = (msg: string) => {
    snackbar?.open(msg, "error");
  };

  const handleDownload = async (key: string, name: string) => {
    try {
      fetchData(
        `/cloud/download?key=${key}`,
        {
          method: "GET",
        },
        false,
      )
        .then((response) => response.blob())
        .then((blob) => URL.createObjectURL(blob))
        .then((url) => {
          const link = document.createElement("a");
          link.href = url;
          link.download = name;

          document.body.appendChild(link);

          link.dispatchEvent(
            new MouseEvent("click", {
              bubbles: true,
              cancelable: true,
              view: window,
            }),
          );

          document.body.removeChild(link);
        })
        .catch((error) => {
          handleError("Ops! No momento não foi possível baixar o arquivo.");
        });
    } catch (error) {
      handleError("Ops! No momento não foi possível baixar o arquivo.");
    }
  };

  const handleDelete = async (key: string) => {
    try {
      fetchData(
        `/cloud?key=${key}`,
        {
          method: "DELETE",
        },
        false,
      )
        .then((response) => {
          setDrawer(drawerDataDefault);
          handleCloudApi();
          snackbar?.open("Arquivo excluído com sucesso!", "success");
        })
        .catch((error) => {
          handleError("Ops! Não foi possível excluir o arquivo.");
        });
    } catch (error) {
      handleError("Ops! Não foi possível excluir o arquivo.");
    }
  };

  const handleLinkCopy = async (key: string) => {
    try {
      fetchData(`/cloud/share?key=${key}`, {
        method: "GET",
      })
        .then((response) => {
          navigator.clipboard.writeText(response.url);
          snackbar?.open("Link copiado com sucesso!", "success");
        })
        .catch((error) => {
          handleError("Ops! Não foi possível gerar o link.");
        });
    } catch (error) {
      handleError("Ops! Não foi possível gerar o link.");
    }
  };

  const loadAccountingDraft = async () => {
    const orgToUse = organizationData || organization;
    const organizationId = id || orgToUse?._id;
    const organizationRef = orgToUse?.reference;

    if (!isAccountingPath || !organizationRef || !organizationId) {
      setAccountingDraft(null);
      return;
    }

    setLoadingDraft(true);
    try {
      const pathParts = decodedPath?.split("/");
      if (pathParts && pathParts.length >= 3) {
        const yyyy = pathParts[1];
        const mm = pathParts[2];
        const period = `${yyyy}-${mm.padStart(2, "0")}`;

        const draftsResponse = await fetchData(
          `/accounting/drafts?limit=1000&company=${organizationId}`,
          {
            method: "GET",
          },
        );

        if (draftsResponse?.rows && Array.isArray(draftsResponse.rows)) {
          const draft = draftsResponse.rows.find(
            (d: any) => d.period === period,
          );
          setAccountingDraft(draft || null);
        } else {
          setAccountingDraft(null);
        }
      } else {
        setAccountingDraft(null);
      }
    } catch (error) {
      console.error("Erro ao carregar draft contábil:", error);
      setAccountingDraft(null);
    } finally {
      setLoadingDraft(false);
    }
  };

  const handleValidate = async () => {
    if (!accountingDraft?._id) return;

    setValidating(true);
    const userName = auth?.user?.name || "CRM";
    const comment = `Usuário '${userName}' validou os documentos. Status alterado para 'Processando'.`;
    try {
      await fetchData(`/accounting/drafts/${accountingDraft._id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "processing", comment }),
      });
      snackbar?.open("Documentos validados com sucesso!", "success");
      await loadAccountingDraft();
    } catch (error) {
      handleError("Ops! Não foi possível validar os documentos.");
    } finally {
      setValidating(false);
    }
  };

  const handleValidateOpen = () => {
    setValidateConfirmOpen(true);
  };

  const handleValidateConfirmClose = () => {
    setValidateConfirmOpen(false);
  };

  const handleInvalidateOpen = () => {
    setInvalidateMessage("");
    setInvalidateDialogOpen(true);
  };

  const handleInvalidateDialogClose = () => {
    setInvalidateDialogOpen(false);
    setInvalidateMessage("");
  };

  const handleInvalidateDialogConfirm = () => {
    if (!invalidateMessage.trim()) return;
    setInvalidateDialogOpen(false);
    setInvalidateConfirmOpen(true);
  };

  const handleInvalidateConfirmClose = () => {
    setInvalidateConfirmOpen(false);
    setInvalidateMessage("");
  };

  const handleInvalidate = async () => {
    if (!accountingDraft?._id) return;

    setInvalidateConfirmOpen(false);
    setValidating(true);
    const userName = auth?.user?.name || "CRM";
    const comment = `Usuário '${userName}' invalidou os documentos.\n\nMensagem do usuário:\n\n'${invalidateMessage}'`;
    try {
      await fetchData(`/accounting/drafts/${accountingDraft._id}`, {
        method: "PATCH",
        body: JSON.stringify({
          status: "pending",
          taskComment: invalidateMessage,
          comment,
        }),
      });
      setInvalidateMessage("");
      snackbar?.open("Documentos invalidados com sucesso!", "success");
      await loadAccountingDraft();
    } catch {
      handleError("Ops! Não foi possível invalidar os documentos.");
    } finally {
      setValidating(false);
    }
  };

  useEffect(() => {
    handleCloudApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadOrganization = async () => {
      if (id) {
        if (organization?._id === id && organization?.reference) {
          setOrganizationData(organization);
        } else {
          try {
            const orgData = await fetchData(`/organizations/${id}`, {
              method: "GET",
            });
            setOrganizationData(orgData);
          } catch (error) {
            console.error("Erro ao carregar organização:", error);
            setOrganizationData(null);
          }
        }
      } else if (organization?._id && organization?.reference) {
        setOrganizationData(organization);
      } else {
        setOrganizationData(null);
      }
    };
    loadOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, organization?._id, organization?.reference]);

  useEffect(() => {
    const orgToUse = organizationData || organization;
    if (isAccountingPath && (id || orgToUse?._id) && orgToUse?.reference) {
      loadAccountingDraft();
    } else {
      setAccountingDraft(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    decodedPath,
    id,
    organizationData,
    organization?._id,
    organization?.reference,
  ]);

  useEffect(() => {
    if (!drawer.state) {
      setDrawer(drawerDataDefault);
    }
  }, [drawer]);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Nome",
      flex: 1,
      sortable: false,
      renderCell: (params: any) => {
        return (
          // <Link
          //   onClick={(e) => {
          //     e.preventDefault();
          //     handleDownload(params.row.key, params.row.name);
          //   }}
          //   sx={{
          //     overflow: "hidden",
          //     justifyContent: "left",
          //     cursor: "pointer",
          //   }}
          // >
          <Stack
            direction="row"
            spacing={1}
            color="primary.main"
            sx={{ cursor: "default" }}
          >
            <InsertDriveFileIcon />
            <Typography variant="body2">{params.row.name}</Typography>
          </Stack>
          // </Link>
        );
      },
    },
    {
      field: "lastModifiedFormated",
      headerName: "Última modificação",
      minWidth: 170,
      sortable: false,
    },
    {
      field: "size",
      headerName: "Tamanho",
      sortable: false,
      renderCell: (params: any) => humanFileSize(params.row.size),
    },
  ];

  const handleRowClick = (params: any) => {
    setDrawer({ ...drawer, state: true, data: params.row });
  };

  return (
    <Box sx={{ p: boxPadding, position: "relative" }}>
      {loading && <LinearProgress />}

      {!data.files.length && !loading && (
        <Alert severity="info">Nenhum arquivo enviado até o momento.</Alert>
      )}

      {data.files.length > 0 && !loading && (
        <DataGrid
          rows={data.files}
          columns={columns}
          getRowId={(row) => row.key || row.id || `${row.name}-${row.size}`}
          onRowClick={handleRowClick}
          autoHeight
          hideFooter
          disableColumnMenu={true}
          disableColumnSelector={true}
          sx={{ border: "none", marginTop: "-10px" }}
        />
      )}

      {isAccountingPath &&
        !loadingDraft &&
        accountingDraft?.status === "queued" &&
        !validationInfoDismissed && (
          <Dialog
            open={true}
            onClose={() => setValidationInfoDismissed(true)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Validação de documentos</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Nesta tela você pode <strong>validar</strong> ou{" "}
                <strong>invalidar</strong> os documentos anexados pelo cliente.
              </DialogContentText>
              <DialogContentText sx={{ mb: 1 }}>
                <strong>Invalidar:</strong> use quando houver pendências. Você
                digita o que está incorreto ou faltando e essa mensagem é
                enviada ao cliente para que ele complemente ou corrija os
                anexos.
              </DialogContentText>
              <DialogContentText>
                <strong>Validar:</strong> use quando você conferiu os documentos
                e eles estão aptos para seguir para o processamento contábil.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setValidationInfoDismissed(true)}
              >
                Entendi
              </Button>
            </DialogActions>
          </Dialog>
        )}

      {isAccountingPath &&
        !loadingDraft &&
        accountingDraft?.status === "queued" &&
        validationInfoDismissed && (
          <Box
            sx={{
              position: "fixed",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1300,
              backgroundColor: "background.paper",
              borderRadius: 1,
              boxShadow: 3,
              p: 2,
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={
                  validating ? (
                    <CircularProgress size={16} sx={{ color: "#FFF" }} />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                onClick={handleValidateOpen}
                disabled={validating}
              >
                Validar
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={
                  validating ? (
                    <CircularProgress size={16} sx={{ color: "#FFF" }} />
                  ) : (
                    <CancelIcon />
                  )
                }
                onClick={handleInvalidateOpen}
                disabled={validating}
              >
                Invalidar
              </Button>
            </Stack>
          </Box>
        )}

      <Dialog
        open={validateConfirmOpen}
        onClose={handleValidateConfirmClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar validação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja validar os documentos? O período será enviado
            para processamento.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleValidateConfirmClose} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              await handleValidate();
              handleValidateConfirmClose();
            }}
            variant="contained"
            color="success"
            disabled={validating}
          >
            {validating ? (
              <CircularProgress size={20} sx={{ color: "inherit" }} />
            ) : (
              "Sim, validar"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={invalidateDialogOpen}
        onClose={handleInvalidateDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Descreva abaixo as pendências</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              mt: 1,
              maxHeight: 220,
              overflowY: "auto",
              width: "100%",
            }}
          >
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              multiline
              minRows={4}
              maxRows={12}
              required
              placeholder="Digite as pendências para o cliente."
              value={invalidateMessage}
              onChange={(e) => setInvalidateMessage(e.target.value)}
              variant="outlined"
              sx={{
                "& .MuiInputBase-root": { alignItems: "flex-start" },
                "& .MuiInputBase-input": { overflowY: "auto", maxHeight: 180 },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInvalidateDialogClose} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={handleInvalidateDialogConfirm}
            variant="contained"
            color="primary"
            disabled={!invalidateMessage.trim()}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={invalidateConfirmOpen}
        onClose={handleInvalidateConfirmClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar envio</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja enviar esta mensagem? O cliente irá
            recebê-la.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInvalidateConfirmClose} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={handleInvalidate}
            variant="contained"
            color="primary"
            disabled={validating}
          >
            {validating ? (
              <CircularProgress size={20} sx={{ color: "inherit" }} />
            ) : (
              "Sim, enviar"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={drawer.state}
        onClose={() => {
          setDrawer({ ...drawer, state: false });
        }}
      >
        <Box sx={{ p: 3, pt: 2, width: 300 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Stack
              direction="row"
              spacing={1}
              sx={{ flex: 1 }}
              alignItems="center"
            >
              <InsertDriveFileIcon />
              <Typography
                variant="body2"
                sx={{ width: 170, wordWrap: "break-word" }}
              >
                {drawer.data.name}
              </Typography>
            </Stack>
            <IconButton
              onClick={() => {
                setDrawer({ ...drawer, state: false });
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack direction="column" spacing={2}>
            <Button
              variant="outlined"
              fullWidth
              size="small"
              onClick={() => {
                handleDownload(drawer.data.key, drawer.data.name);
              }}
            >
              Download
            </Button>
            <Button
              variant="outlined"
              color="success"
              fullWidth
              size="small"
              onClick={() => {
                handleLinkCopy(drawer.data.key);
              }}
            >
              Copiar Link
            </Button>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body1">Detalhes do arquivo</Typography>
          <br />
          <Stack direction="row" spacing={1}>
            <Typography variant="caption" fontWeight="bold" sx={{ flex: 1 }}>
              Tipo
            </Typography>
            <Typography variant="caption">
              {drawer.data.name?.split(".").pop()?.toLocaleUpperCase()}
            </Typography>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={1}>
            <Typography variant="caption" fontWeight="bold" sx={{ flex: 1 }}>
              Tamanho
            </Typography>
            <Typography variant="caption">
              {humanFileSize(drawer.data.size)}
            </Typography>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={1}>
            <Typography variant="caption" fontWeight="bold" sx={{ flex: 1 }}>
              Modificado
            </Typography>
            <Typography variant="caption">
              {drawer.data.lastModifiedFormated}
            </Typography>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="column">
            <Typography variant="caption" fontWeight="bold" sx={{ flex: 1 }}>
              ID
            </Typography>
            <Typography variant="caption">
              {drawer.data.id.replace(/['"]+/g, "")}
            </Typography>
          </Stack>
          <Divider sx={{ my: 2 }} />
          {!drawer.delete && auth?.user.permission_level === "manager" && (
            <Button
              variant="outlined"
              color="error"
              fullWidth
              size="small"
              onClick={() => {
                setDrawer({ ...drawer, delete: true });
              }}
            >
              Apagar arquivo
            </Button>
          )}
          {drawer.delete && (
            <Stack direction="column" spacing={1}>
              <Typography variant="body2" fontWeight="bold" textAlign="center">
                Confirme a exclusão do arquivo?
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  size="small"
                  onClick={() => {
                    setDrawer({ ...drawer, delete: false });
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  size="small"
                  onClick={() => {
                    handleDelete(drawer.data.key);
                  }}
                >
                  Confirmar
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

OrganizationCloudTab.defaultProps = {
  path: undefined,
  id: undefined,
  boxPadding: 2,
};

export default OrganizationCloudTab;
